import { h } from 'preact';
import {
	useState, useContext, useMemo, cloneElement,
} from 'preact/compat';
import { useCart } from '../../hooks/cart';
import { Theme } from '../../lib/render';
import { useSubmit } from '../../hooks/submit';
import UserDetails from './UserDetails';
import ConfirmNavigation from './ConfirmNavigation';
import { safegaurd } from '../../hooks/candidateSafeGaurds';

export enum ConfirmationStepEnum {
	DETAILS = 'details',
	UPSELL = 'upsell',
	FINAL_CONFIRM = 'confirm',
}

/**
 * Confirmation Flow.
 * Has own internal state to handle the sub steps within the checkout.
 * The final step has button to submit form.
 * We unmount this component when user navs away from confirm flow,
 * this resets user postion back to confirming their details.
 *
 * @param {h.JSX.Element} Upsell The upsell component to render. Allows for custom upsell.
 */
const Confirmation = ({
	Upsell,
}: {
	Upsell: h.JSX.Element
}) => {
	const theme = useContext(Theme);
	const { dispatch, cart } = useCart();
	const [state, setState] = useState(ConfirmationStepEnum.DETAILS);
	const { isSubmitting } = useSubmit();
	const {
		total,
	} = cart;

	/**
	 * Confirm Upsell is valid.
	 * Check if upsell is active.
	 * Check if upsell data is valid.
	 * Check general safegaurd.
	 */
	const isUpsellValid = useMemo(() => {
		if (!theme.hasUpsell || theme.upsell === null) {
			return false;
		}
		return safegaurd(theme.upsell.target);
	}, [theme]);

	/**
	 * Local state selection.
	 * Handles conditional upsell.
	 */
	const backTarget = useMemo(() => {
		let back = ConfirmationStepEnum.DETAILS;
		if (state === ConfirmationStepEnum.FINAL_CONFIRM && isUpsellValid) {
			back = ConfirmationStepEnum.UPSELL;
		}
		return back;
	}, [state]);

	/**
	 * Clone upsell and pass in setState.
	 */
	const UpsellClone = cloneElement(Upsell, {
		setState,
	});

	return (
		<div className="ea-confirm">
			{(state === ConfirmationStepEnum.DETAILS || !isUpsellValid) && <UserDetails />}
			{(state !== ConfirmationStepEnum.DETAILS && isUpsellValid) && UpsellClone}
			<ConfirmNavigation
				isSubmitting={isSubmitting}
				state={state}
				setState={setState}
				dispatch={dispatch}
				isUpsellValid={isUpsellValid}
				backTarget={backTarget}
				theme={theme}
				total={total}
				cart={cart}
			/>
		</div>
	);
};

export default Confirmation;
